import { getNamespacedResource } from "SharedStores/util";
import { idGenerator } from "Util/uniqueIdGenerator";
import { RULE_TYPE, RULE_FREQUENCY, TRIGGER_ACTION_TYPE } from "Types/automationTypes";
import createOrEditRule from "./createOrEditRule.vue";
import { DIALOG_HOOK, DIALOG_RESULT } from "Components/pc-dialog.vue";

import { KEYS, NAMESPACE } from "./i18n/editAutomation.i18n";
import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";

const EDIT_AUTOMATION_NAMESPACE = "editAutomation";

//getters
const GET_RULES_BY_TYPE = "getRulesByType";
const ACTION_RULES = "actionRules";
const VALIDATION_RULES = "validationRules";
const PROGRAM_RULES = "programRules";
const ALLOW_CASCADING_RULES = "allowCascadingRules";
const CAN_EDIT_RULE_TYPE = "canEditRuleType";

//mutations
const REORDER_RULES = "reorderRules";
const COMMIT_TRIGGER = "commitTrigger";
const COMMIT_PUBLICID = "commitPublicId";
const DELETE_RULE = "deleteRule";

//actions
const EDIT_RULE = "editRule";
const ADD_RULE = "addRule";

const EDIT_AUTOMATION_TYPES = {
    getters: {
        [GET_RULES_BY_TYPE]: getNamespacedResource(EDIT_AUTOMATION_NAMESPACE, GET_RULES_BY_TYPE),
        [ACTION_RULES]: getNamespacedResource(EDIT_AUTOMATION_NAMESPACE, ACTION_RULES),
        [VALIDATION_RULES]: getNamespacedResource(EDIT_AUTOMATION_NAMESPACE, VALIDATION_RULES),
        [PROGRAM_RULES]: getNamespacedResource(EDIT_AUTOMATION_NAMESPACE, PROGRAM_RULES),
        [ALLOW_CASCADING_RULES]: getNamespacedResource(EDIT_AUTOMATION_NAMESPACE, ALLOW_CASCADING_RULES),
        [CAN_EDIT_RULE_TYPE]: getNamespacedResource(EDIT_AUTOMATION_NAMESPACE, CAN_EDIT_RULE_TYPE)
    },
    mutations: {
        [REORDER_RULES]: getNamespacedResource(EDIT_AUTOMATION_NAMESPACE, REORDER_RULES),
        [COMMIT_TRIGGER]: getNamespacedResource(EDIT_AUTOMATION_NAMESPACE, COMMIT_TRIGGER),
        [COMMIT_PUBLICID]: getNamespacedResource(EDIT_AUTOMATION_NAMESPACE, COMMIT_PUBLICID),
        [DELETE_RULE]: getNamespacedResource(EDIT_AUTOMATION_NAMESPACE, DELETE_RULE)
    },
    actions: {
        [EDIT_RULE]: getNamespacedResource(EDIT_AUTOMATION_NAMESPACE, EDIT_RULE),
        [ADD_RULE]: getNamespacedResource(EDIT_AUTOMATION_NAMESPACE, ADD_RULE)
    }
};

export { EDIT_AUTOMATION_NAMESPACE, EDIT_AUTOMATION_TYPES };

const clientIdGenerator = new idGenerator();

function getNextRuleId() {
    return clientIdGenerator.getNextId("pcEditAutomationRule");
}

function getRulesById(ruleList) {
    const rulesById = {};
    ruleList.forEach((rule) => {
        rulesById[rule.clientId] = rule;
    });
    return rulesById;
}

async function showEditRuleDialog({ $app, rule, ruleType, title }) {
    const { action, data } = await $app.showDialog({
        title: title,
        customComponent: createOrEditRule,
        customComponentOptions: {
            rule,
            ruleType
        },
        customComponentResultHook: DIALOG_HOOK.getResult,
        customComponentIsValidToCloseHookAsync: DIALOG_HOOK.isValidToClose,
        sidePanel: true,
        testRole: "showEditRuleDialog",
        buttons: [
            {
                text: $app.res({ namespace: COMMON_NAMESPACE, key: COMMON_KEYS.cancelText }),
                result: DIALOG_RESULT.cancel
            },
            {
                text: $app.res({ namespace: COMMON_NAMESPACE, key: COMMON_KEYS.okText }),
                primary: true,
                result: DIALOG_RESULT.ok
            }
        ]
    });

    if (action === DIALOG_RESULT.cancel) {
        return;
    }

    if (action === DIALOG_RESULT.ok) {
        return data;
    }
}

export function createEditAutomationModule({
    $app,
    Automation,
    StatusContexts,
    FormContexts,
    FieldTypes,
    FormulaTypes
}) {
    // create clientIds for rules so that we can turn the lists into maps
    Automation.ActionRules.forEach((rule) => {
        rule.clientId = getNextRuleId();
    });
    Automation.ValidationRules.forEach((rule) => {
        rule.clientId = getNextRuleId();
    });
    Automation.ProgramRules.forEach((rule) => {
        rule.clientId = getNextRuleId();
    });

    // split up each of the pieces of the automation so that we dont have to deal with nested objects as much
    const state = {
        PublicId: Automation.PublicId,
        Trigger: Automation.Trigger,
        Enabled: Automation.Enabled,
        actionRulesById: getRulesById(Automation.ActionRules),
        actionRuleIdsInOrder: Automation.ActionRules.map((r) => r.clientId),
        validationRulesById: getRulesById(Automation.ValidationRules),
        validationRuleIdsInOrder: Automation.ValidationRules.map((r) => r.clientId),
        programRulesById: getRulesById(Automation.ProgramRules),
        programRuleIdsInOrder: Automation.ProgramRules.map((r) => r.clientId),
        isCPAdmin: $app.loggedInUserIsCPAdmin,

        StatusContexts,
        FormContexts,
        FieldTypes,
        FormulaTypes
    };
    return {
        namespaced: true,
        state,
        getters: {
            [GET_RULES_BY_TYPE](state, getters) {
                return function (ruleType) {
                    switch (ruleType) {
                        case RULE_TYPE.programWideProject:
                        case RULE_TYPE.action:
                            return getters[ACTION_RULES];
                        case RULE_TYPE.validation:
                            return getters[VALIDATION_RULES];
                        case RULE_TYPE.program:
                            return getters[PROGRAM_RULES];
                        default:
                            return null;
                    }
                };
            },
            [ACTION_RULES](state) {
                return state.actionRuleIdsInOrder.map((clientId) => state.actionRulesById[clientId]);
            },
            [VALIDATION_RULES](state) {
                return state.validationRuleIdsInOrder.map((clientId) => state.validationRulesById[clientId]);
            },
            [PROGRAM_RULES](state) {
                return state.programRuleIdsInOrder.map((clientId) => state.programRulesById[clientId]);
            },
            [ALLOW_CASCADING_RULES](state) {
                return (
                    state.Trigger.ActionType !== TRIGGER_ACTION_TYPE.ExitStatus &&
                    state.Trigger.ActionType !== TRIGGER_ACTION_TYPE.StatusTransition
                );
            },
            [CAN_EDIT_RULE_TYPE](state) {
                // Don't allow non-cpadmins to edit program wide project action rules
                return function (ruleType) {
                    switch (ruleType) {
                        case RULE_TYPE.programWideProject:
                            return state.isCPAdmin;
                        case RULE_TYPE.program:
                        case RULE_TYPE.action:
                        case RULE_TYPE.validation:
                        default:
                            return true;
                    }
                };
            }
        },
        mutations: {
            [REORDER_RULES](state, { clientIds }) {
                // only action rules are orderable, so barring a bug we can assume these rules are in ActionRules
                clientIds.forEach((clientId, index) => {
                    const rule = state.actionRulesById[clientId];
                    rule.Order = index;
                });
                state.actionRuleIdsInOrder = clientIds;
            },
            [COMMIT_PUBLICID](state, publicId) {
                state.PublicId = publicId;
            },
            [COMMIT_TRIGGER](state, trigger) {
                state.Trigger = trigger;
            },
            commitRule(state, { ruleType, newRuleValues }) {
                const ruleId = newRuleValues.clientId;
                let rule = null;
                switch (ruleType) {
                    case RULE_TYPE.programWideProject:
                    case RULE_TYPE.action:
                        rule = state.actionRulesById[ruleId];
                        break;
                    case RULE_TYPE.validation:
                        rule = state.validationRulesById[ruleId];
                        break;
                    case RULE_TYPE.program:
                        rule = state.programRulesById[ruleId];
                        break;
                    default:
                        break;
                }

                rule.FormulaId = newRuleValues.FormulaId;
                rule.Order = newRuleValues.Order;
                rule.StopFurtherProcessing = newRuleValues.StopFurtherProcessing;
                rule.Frequency = newRuleValues.Frequency;
                rule.ErrorMessage = newRuleValues.ErrorMessage;
                rule.Action = newRuleValues.Action;
                $app.setUnsavedChanges(true);
            },
            addRule(state, { ruleType, newRule }) {
                const ruleId = newRule.clientId;

                switch (ruleType) {
                    case RULE_TYPE.programWideProject:
                    case RULE_TYPE.action:
                        newRule.Order = state.actionRuleIdsInOrder.length;
                        state.actionRulesById[ruleId] = newRule;
                        state.actionRuleIdsInOrder.push(ruleId);
                        break;
                    case RULE_TYPE.validation:
                        state.validationRulesById[ruleId] = newRule;
                        state.validationRuleIdsInOrder.push(ruleId);
                        break;
                    case RULE_TYPE.program:
                        state.programRulesById[ruleId] = newRule;
                        state.programRuleIdsInOrder.push(ruleId);
                        break;
                    default:
                        break;
                }
                $app.setUnsavedChanges(true);
            },
            [DELETE_RULE](state, { ruleType, ruleId }) {
                switch (ruleType) {
                    case RULE_TYPE.programWideProject:
                    case RULE_TYPE.action:
                        delete state.actionRulesById[ruleId];
                        state.actionRuleIdsInOrder.splice(state.actionRuleIdsInOrder.indexOf(ruleId), 1);
                        state.actionRuleIdsInOrder.forEach((ruleId, index) => {
                            const rule = state.actionRulesById[ruleId];
                            rule.Order = index;
                        });
                        break;
                    case RULE_TYPE.validation:
                        delete state.validationRulesById[ruleId];
                        state.validationRuleIdsInOrder.splice(state.validationRuleIdsInOrder.indexOf(ruleId), 1);
                        break;
                    case RULE_TYPE.program:
                        delete state.programRulesById[ruleId];
                        state.programRuleIdsInOrder.splice(state.programRuleIdsInOrder.indexOf(ruleId), 1);
                        break;
                    default:
                        break;
                }
                $app.setUnsavedChanges(true);
            }
        },
        actions: {
            async [EDIT_RULE]({ commit, state }, { ruleType, ruleId }) {
                let rule = null;
                let titleKey = null;
                switch (ruleType) {
                    case RULE_TYPE.programWideProject:
                        titleKey = KEYS.editProgramWideActionText;
                        rule = state.actionRulesById[ruleId];
                        break;
                    case RULE_TYPE.action:
                        titleKey = KEYS.editActionText;
                        rule = state.actionRulesById[ruleId];
                        break;
                    case RULE_TYPE.validation:
                        titleKey = KEYS.editValidationText;
                        rule = state.validationRulesById[ruleId];
                        break;
                    case RULE_TYPE.program:
                        titleKey = KEYS.editProgramActionText;
                        rule = state.programRulesById[ruleId];
                        break;
                }
                if (!rule || !titleKey) {
                    return;
                }
                const editedRuleCopy = await showEditRuleDialog({
                    $app,
                    rule,
                    ruleType,
                    title: $app.res({ namespace: NAMESPACE, key: titleKey })
                });
                if (!editedRuleCopy) {
                    return;
                }
                commit("commitRule", { ruleType, newRuleValues: editedRuleCopy });
            },
            async [ADD_RULE]({ commit }, { ruleType }) {
                const rule = {
                    clientId: getNextRuleId(),
                    FormulaId: null,
                    Order: null,
                    StopFurtherProcessing: null,
                    Frequency: RULE_FREQUENCY.EveryTime,
                    ErrorMessage: null,
                    Action: {
                        Type: null,
                        StatusId: null,
                        DataId: null,
                        DataFieldValue: null,
                        AppendConsecutiveNumber: null,
                        AdapterConfigurationId: null,
                        DeadlineId: null,
                        SatisfyAllDeadlines: null,
                        CommunicationTemplateId: null,
                        ReportId: null,
                        DocumentTemplateId: null,
                        AttachmentId: null,
                        GenerateAsDocx: null,
                        SourceDataId: null,
                        SourceFormulaId: null,
                        ChannelId: null,
                        ChannelRuleId: null,
                        RemoveAllAccessGroups: null,
                        AccessGroupId: null,
                        EnvelopeDefinitionId: null
                    }
                };

                let titleKey = null;
                switch (ruleType) {
                    case RULE_TYPE.programWideProject:
                        titleKey = KEYS.addProgramWideActionText;
                        break;
                    case RULE_TYPE.action:
                        titleKey = KEYS.addActionText;
                        break;
                    case RULE_TYPE.validation:
                        titleKey = KEYS.addValidationText;
                        break;
                    case RULE_TYPE.program:
                        titleKey = KEYS.addProgramActionText;
                        break;
                }
                if (!titleKey) {
                    return;
                }
                const addedRuleCopy = await showEditRuleDialog({
                    $app,
                    rule,
                    ruleType,
                    title: $app.res({ namespace: NAMESPACE, key: titleKey })
                });
                if (!addedRuleCopy) {
                    return;
                }
                commit("addRule", { ruleType, newRule: addedRuleCopy });
            }
        }
    };
}
